import { useCallback, useRef, useState } from 'react';
import type { ScanResult } from '../engine/types';
import { Badge, Card } from './components';
import type { DecisionResult, Progress } from '../ai/decider';
import { DEFAULT_FAMILY, FAMILIES } from '../ai/families';
import { buildState, QUESTIONS } from '../ai/state';

type Phase = 'idle' | 'loading' | 'running' | 'done' | 'error';

const OPTION_LABELS: Record<string, string> = {
  'certainly a real person': 'Certainly a real person',
  'probably a real person': 'Probably a real person',
  uncertain: 'Uncertain',
  'probably automated': 'Probably automated',
  'certainly automated': 'Certainly automated',
  'not suspicious': 'Not suspicious',
  'slightly suspicious': 'Slightly suspicious',
  'moderately suspicious': 'Moderately suspicious',
  'very suspicious': 'Very suspicious',
  'extremely suspicious': 'Extremely suspicious',
};

export function AiPanel({ result }: { result: ScanResult | null }) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<Progress | null>(null);
  const [decision, setDecision] = useState<DecisionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showState, setShowState] = useState(false);
  const [confirmLarge, setConfirmLarge] = useState(false);
  const [family, setFamily] = useState(DEFAULT_FAMILY);
  const runId = useRef(0);

  const run = useCallback(async () => {
    if (!result) return;
    const id = ++runId.current;
    setError(null);
    setDecision(null);
    setPhase('loading');
    try {
      // Lazy import: keeps the runtime + weights out of the main bundle.
      const { decide } = await import('../ai/decider');
      if (id !== runId.current) return;
      setPhase('running');
      const res = await decide(result, family, setProgress);
      if (id !== runId.current) return;
      setDecision(res);
      setPhase('done');
    } catch (e) {
      if (id !== runId.current) return;
      setError(String((e as Error)?.message ?? e));
      setPhase('error');
    }
  }, [result, family]);

  const selectFamily = useCallback(
    (next: string) => {
      // Cancel anything in flight: a run started under another family must not
      // overwrite this selection's result.
      runId.current += 1;
      setFamily(next);
      setDecision(null);
      setError(null);
      setProgress(null);
      setPhase('idle');
    },
    [],
  );

  const busy = phase === 'loading' || phase === 'running';

  return (
    <Card
      title="AI verdict (experimental)"
      subtitle="On-device text-classifier opinion over the collected signals."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-zinc-400">
            Model
            <select
              value={family}
              disabled={busy}
              onChange={(e) => selectFamily(e.target.value)}
              className="rounded-lg border border-white/10 bg-black/40 px-2 py-1.5 text-xs text-zinc-200 disabled:opacity-50"
            >
              {FAMILIES.map((f) => (
                <option key={f.id} value={f.id} title={f.detail}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => void run()}
            disabled={!result || busy}
            className="rounded-lg border border-orange-500/25 bg-orange-500/10 px-3 py-1.5 text-sm font-medium text-orange-200 transition-colors hover:bg-orange-500/20 disabled:opacity-50"
          >
            {busy ? 'Running…' : decision ? 'Re-run' : 'Run AI analysis'}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs leading-5 text-amber-100/80">
          <strong className="text-amber-200">Read this first.</strong> This runs a JEV-style
          typed-decision model (via <code className="font-mono">jev-web</code>, model family
          selectable above — if your pick cannot load, the runtime falls back to the next
          family automatically) that was trained on natural-language text, over a prose rendering of
          your fingerprint signals. That is a deliberate best-effort, but it is still outside the
          model's training distribution — so treat the result as a curiosity, not a verdict. It never
          contributes to the bot score, and the deterministic rule engine remains the source of
          truth. The model runs entirely in your browser; nothing is uploaded.
        </div>

        {phase === 'idle' && (
          <div className="space-y-2 text-sm text-zinc-400">
            <p>
              The selected model ({FAMILIES.find((f) => f.id === family)?.detail}) is downloaded
              only when you click, and cached by the browser afterwards. Switching models keeps
              each one cached, so going back is fast.
            </p>
            {!confirmLarge && (
              <label className="flex items-center gap-2 text-xs text-zinc-500">
                <input
                  type="checkbox"
                  checked={confirmLarge}
                  onChange={(e) => setConfirmLarge(e.target.checked)}
                  className="accent-orange-500"
                />
                I understand this downloads a few hundred megabytes.
              </label>
            )}
          </div>
        )}

        {busy && (
          <div className="space-y-2 text-sm text-zinc-300">
            <div className="flex items-center gap-2">
              <Badge tone="denied">{phase === 'loading' ? 'loading model' : 'deciding'}</Badge>
              {progress?.file && <span className="text-xs text-zinc-500">{progress.file}</span>}
            </div>
            {progress?.total ? (
              <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-800">
                <div
                  className="h-full rounded-full bg-orange-500/70 transition-all"
                  style={{
                    width: `${Math.min(100, ((progress.loaded ?? 0) / progress.total) * 100)}%`,
                  }}
                />
              </div>
            ) : (
              <p className="text-xs text-zinc-500">
                First run downloads and caches the weights; later runs are fast and offline.
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="rounded-lg border border-rose-500/20 bg-rose-500/5 p-3 text-sm text-rose-200">
            {error}
          </p>
        )}

        {decision && (
          <div className="space-y-3">
            <DecisionAnswers answers={decision.answers} />
            <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
              {decision.info?.family ? (
                <span className="font-mono text-zinc-400">family: {String(decision.info.family)}</span>
              ) : null}
              {decision.info?.model ? (
                <span className="font-mono">{String(decision.info.model)}</span>
              ) : null}
              <span>
                state tokens: {decision.length}
                {decision.truncated ? ' (truncated)' : ''}
              </span>
              {decision.info?.device ? <span>device: {String(decision.info.device)}</span> : null}
              {decision.info?.dtype ? <span>dtype: {String(decision.info.dtype)}</span> : null}
              {decision.totalMs ? <span>inference: {Math.round(decision.totalMs)}ms</span> : null}
              <button
                type="button"
                onClick={() => setShowState((s) => !s)}
                className="text-orange-300 underline underline-offset-2"
              >
                {showState ? 'hide' : 'view'} the exact text sent to the model
              </button>
            </div>
            {showState && result && (
              <pre className="max-h-80 overflow-auto rounded-lg border border-white/8 bg-black/40 p-3 text-[11px] leading-5 text-zinc-300">
                {buildState(result)}
              </pre>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function DecisionAnswers({ answers }: { answers: DecisionResult['answers'] }) {
  return (
    <div className="space-y-2">
      {answers.map((a, i) => {
        const q = QUESTIONS[i];
        const top = Object.entries(a.probabilities ?? {}).sort((x, y) => y[1] - x[1])[0];
        return (
          <div key={i} className="rounded-lg border border-white/6 bg-black/20 p-3">
            <div className="text-xs text-zinc-400">{q?.instructions}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge tone={toneFor(a)}>{labelFor(a)}</Badge>
              <span className="text-[11px] text-zinc-500">
                confidence {(a.confidence ?? 0).toFixed(2)}
                {top ? ` · top: ${top[0]} ${(top[1] * 100).toFixed(0)}%` : ''}
              </span>
            </div>
            {a.probabilities && Object.keys(a.probabilities).length > 1 && (
              <div className="mt-2 space-y-1">
                {Object.entries(a.probabilities)
                  .sort((x, y) => y[1] - x[1])
                  .map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <span className="w-40 shrink-0 truncate text-[11px] text-zinc-500">{k}</span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800">
                        <div className="h-full rounded-full bg-orange-500/60" style={{ width: `${v * 100}%` }} />
                      </div>
                      <span className="w-10 text-right text-[11px] tabular-nums text-zinc-500">
                        {(v * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function labelFor(a: DecisionResult['answers'][number]): string {
  if (a.type === 'noul') return a.noul! >= 0.5 ? 'Yes' : 'No';
  if (a.type === 'choice') return OPTION_LABELS[a.choice ?? ''] ?? a.choice ?? '—';
  if (a.type === 'score') {
    // Map the continuous score back to the nearest option label.
    const opts = ['not suspicious', 'slightly suspicious', 'moderately suspicious', 'very suspicious', 'extremely suspicious'];
    const idx = Math.max(0, Math.min(opts.length - 1, Math.round(a.score ?? 0)));
    return OPTION_LABELS[opts[idx]];
  }
  return '—';
}

function toneFor(a: DecisionResult['answers'][number]): 'ok' | 'denied' | 'error' {
  if (a.type === 'noul') {
    // Positive = "yes" — for the contradiction/automated questions that's bad.
    const risky = a.noul! >= 0.5;
    return risky ? 'error' : 'ok';
  }
  if (a.type === 'choice') {
    return /automated/i.test(a.choice ?? '') ? 'error' : /real person/i.test(a.choice ?? '') ? 'ok' : 'denied';
  }
  const s = a.score ?? 0;
  return s >= 2.5 ? 'error' : s >= 1.5 ? 'denied' : 'ok';
}
